/**
 * useEndpointActivity - Hook for tracking endpoint activity (blinking)
 *
 * Detects value changes in state.values (from useScope) and provides
 * a Set of active endpoint keys for visual feedback on data endpoints.
 *
 * Keys are in format "nodeId.endpointId:in" or "nodeId.endpointId:out"
 * to distinguish input vs output activity.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useScope } from '../../contexts/ScopeContext';

const BLINK_DURATION_MS = 50;

export function useEndpointActivity() {
  const { instanceState } = useScope();
  const [activeEndpoints, setActiveEndpoints] = useState<Set<string>>(new Set());

  // Track previous values to detect changes (nested: nodeId -> epKey -> value)
  const prevValuesRef = useRef<Record<string, Record<string, any>>>({});
  const expirations = useRef<Map<string, number>>(new Map());
  const pendingActivations = useRef<string[]>([]);
  const frameRef = useRef<number | null>(null);

  // Animation loop - processes blink expirations
  const tick = useCallback(() => {
    const now = performance.now();

    const toActivate = pendingActivations.current;
    pendingActivations.current = [];

    const toAdd: string[] = [];
    const toRemove: string[] = [];

    // Add new activations
    for (const key of toActivate) {
      toAdd.push(key);
      expirations.current.set(key, now + BLINK_DURATION_MS);
    }

    // Check for expired activations
    for (const [key, expiresAt] of expirations.current) {
      if (now >= expiresAt) {
        toRemove.push(key);
        expirations.current.delete(key);
      }
    }

    if (toAdd.length > 0 || toRemove.length > 0) {
      setActiveEndpoints(prev => {
        const next = new Set(prev);
        for (const key of toAdd) next.add(key);
        for (const key of toRemove) next.delete(key);
        return next;
      });
    }

    if (expirations.current.size > 0 || pendingActivations.current.length > 0) {
      frameRef.current = requestAnimationFrame(tick);
    } else {
      frameRef.current = null;
    }
  }, []);

  const ensureLoopRunning = useCallback(() => {
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  // Detect value changes and trigger activity
  // instanceState.values is nested: { nodeId: { endpointId:in: value, endpointId:out: value } }
  useEffect(() => {
    const currentValues = instanceState?.values ?? {};
    const prevValues = prevValuesRef.current ?? {};

    for (const [nodeId, endpoints] of Object.entries(currentValues)) {
      const prevEndpoints = prevValues[nodeId] ?? {};
      for (const [epKey, value] of Object.entries(endpoints as Record<string, any>)) {
        // epKey is like "frequency:in" or "output:out"
        if (prevEndpoints[epKey] !== value) {
          // Return key as "nodeId.endpointId:in" or "nodeId.endpointId:out"
          pendingActivations.current.push(`${nodeId}.${epKey}`);
        }
      }
    }

    if (pendingActivations.current.length > 0) {
      ensureLoopRunning();
    }

    // Deep copy the nested structure
    const copy: Record<string, Record<string, any>> = {};
    for (const [nodeId, endpoints] of Object.entries(currentValues)) {
      copy[nodeId] = { ...(endpoints as Record<string, any>) };
    }
    prevValuesRef.current = copy;
  }, [instanceState?.values, ensureLoopRunning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return activeEndpoints;
}
